import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { optionKeys } from "@/lib/query";

/** One `{ id, label }` option, matching the server `EntityOption` shape. */
export interface EntityOption {
  id: string;
  label: string;
}

export interface EntityOptionsPage {
  data: EntityOption[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export type EntityFilters = Record<string, string | undefined>;

function appendFilters(params: URLSearchParams, filters?: EntityFilters) {
  if (!filters) return;
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
}

interface UseEntityOptionsArgs {
  search?: string;
  filters?: EntityFilters;
  /** Ids to omit from search results (e.g. options the caller already holds). */
  excludeIds?: string[];
  cursor?: string | null;
  limit?: number;
  enabled?: boolean;
}

/**
 * Search + page an entity's options from `/api/options/:entity`. Search is
 * server-side; callers own debouncing (pass an already-debounced `search`).
 * Previous results stay visible while the next query loads.
 */
export function useEntityOptions(entity: string, args: UseEntityOptionsArgs = {}) {
  const { search = "", filters, excludeIds, cursor, limit = 50, enabled = true } = args;
  const excludeKey = excludeIds?.length ? [...excludeIds].sort().join(",") : undefined;
  return useQuery({
    queryKey: [
      ...optionKeys.list(entity, { search, filters, excludeIds: excludeKey }),
      cursor ?? null,
    ] as const,
    queryFn: async (): Promise<EntityOptionsPage> => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (search) params.set("search", search);
      if (cursor) params.set("cursor", cursor);
      if (excludeKey) params.set("excludeIds", excludeKey);
      appendFilters(params, filters);
      const res = await fetch(`/api/options/${entity}?${params}`);
      if (!res.ok) throw new Error("Failed to load options");
      return res.json();
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Resolve the labels for already-selected ids (single or multi) so a picker
 * can render its current value even when those rows aren't in the search page.
 */
export function useEntityOptionLabels(entity: string, ids: string[]) {
  const sorted = [...ids].filter(Boolean).sort();
  const key = sorted.join(",");
  return useQuery({
    queryKey: optionKeys.detail(entity, key),
    queryFn: async (): Promise<EntityOption[]> => {
      const params = new URLSearchParams({ ids: key });
      const res = await fetch(`/api/options/${entity}?${params}`);
      if (!res.ok) throw new Error("Failed to resolve options");
      const page: EntityOptionsPage = await res.json();
      return page.data;
    },
    enabled: sorted.length > 0,
    // Deliberately the 30s default rather than a long cache. This resolves the
    // label shown on a picker's TRIGGER, so a five-minute staleTime is what made
    // a rename look like it hadn't taken: the list underneath refreshed and the
    // selected value kept its old name. The global mutation cache invalidates
    // ["options"], which covers a rename immediately; this only governs how long
    // a label survives without any mutation at all.
  });
}
